import React from 'react';

import PropTypes from 'prop-types';
import { Form, FormGroup } from 'semantic-ui-react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';

import { isAdminUser } from 'libs/auth';
import { selectUser } from 'selectors/user';

import {
  ASPECT_RATIO_TYPES,
  BLUR_MAX,
  BLUR_MIN,
  CROP_TYPES,
  DEFAULT_MAX_HEIGHT,
  DEFAULT_MAX_WIDTH,
  DESKTOP_DEVICE,
  FILL_TYPES,
  MASK_TYPES,
  MODE_TYPES,
  MODE_TYPE_CROP,
  MODE_TYPE_FILL,
  QUALITY_DEFAULT,
  QUALITY_MAX,
  QUALITY_MIN,
} from './ImageQueryParamBuilder.constants';
import { ImageQueryParamBuilderFormDataPropType } from './ImageQueryParamBuilder.types';
import { getOptions } from './ImageQueryParamBuilder.utils';
import QueryParamsDisplay from './components/QueryParamsDisplay';

import './ImageQueryParamBuilder.scss';

const propTypes = {
  formData: ImageQueryParamBuilderFormDataPropType,
  onChange: PropTypes.func,
  url: PropTypes.string,
};

const defaultProps = {
  formData: {},
  onChange: () => {},
  url: '',
};

export default function ImageQueryParamBuilder({ formData, onChange: onChangeProp, url }) {
  const user = useSelector(selectUser);
  const isAdmin = isAdminUser(user);

  const activeDevice = DESKTOP_DEVICE;

  const activeFormData = _get(formData, activeDevice, {});
  const {
    width,
    height,
    mode = '',
    fillType = '',
    cropType = '',
    mask = '',
    aspectRatio = '',
    blur = '',
    upscale = '',
    quality = QUALITY_DEFAULT,
  } = activeFormData;

  function buildNewEvent(e, name, newValue) {
    const { target } = e;

    const newFormData = { ...formData, [activeDevice]: { ...activeFormData, [name]: newValue } };
    const newTarget = { ...target, value: newFormData };
    const newEvent = { ...e, target: newTarget };
    return [newEvent, newTarget];
  }

  function onChange(e, target) {
    const { name, value } = target;

    const [newEvent, newTarget] = buildNewEvent(e, name, value);
    onChangeProp(newEvent, newTarget);
  }

  function onWidthChange(e, target) {
    const { value } = target;
    let newValue = value;

    if (+newValue > +DEFAULT_MAX_WIDTH) {
      newValue = `${DEFAULT_MAX_WIDTH}`;
    }
    if (+newValue < 0) {
      newValue = '1';
    }

    const [newEvent, newTarget] = buildNewEvent(e, 'width', newValue);
    onChangeProp(newEvent, newTarget);
  }

  function onHeightChange(e, target) {
    const { value } = target;
    let newValue = value;

    if (+newValue > +DEFAULT_MAX_HEIGHT) {
      newValue = `${DEFAULT_MAX_HEIGHT}`;
    }
    if (+newValue < 0) {
      newValue = '1';
    }

    const [newEvent, newTarget] = buildNewEvent(e, 'height', newValue);
    onChangeProp(newEvent, newTarget);
  }

  function onModeChange(e, target) {
    const { value } = target;
    let extraData = {};

    if (value !== MODE_TYPE_FILL) {
      extraData = { ...extraData, fillType: '' };
    }
    if (value !== MODE_TYPE_CROP) {
      extraData = { ...extraData, cropType: '', aspectRatio: '' };
    }

    const newFormData = {
      ...formData,
      [activeDevice]: { ...activeFormData, ...extraData, mode: value },
    };
    const newTarget = { ...target, value: newFormData };
    onChangeProp({ ...e, target: newTarget }, newTarget);
  }

  function onAspectRatioChange(e, target) {
    const { value } = target;
    const extraData = { height: '' };

    const newFormData = {
      ...formData,
      [activeDevice]: { ...activeFormData, ...extraData, aspectRatio: value },
    };

    const newTarget = { ...target, value: newFormData };
    onChangeProp({ ...e, target: newTarget }, newTarget);
  }

  function onBlurChange(e, target) {
    const { value } = target;
    const newValue = +value === 0 ? '' : value;
    const [newEvent, newTarget] = buildNewEvent(e, 'blur', newValue);
    onChangeProp(newEvent, newTarget);
  }

  function onQualityChange(e, target) {
    const { value } = target;
    let newValue = +value;
    if (value === '' || Number.isNaN(newValue)) {
      newValue = '';
    } else {
      newValue = Math.max(QUALITY_MIN, Math.min(QUALITY_MAX, newValue));
    }
    const [newEvent, newTarget] = buildNewEvent(e, 'quality', newValue);
    onChangeProp(newEvent, newTarget);
  }

  function onUpscaleChange(e, target) {
    const { checked } = target;
    const newValue = checked ? 'true' : '';
    const [newEvent, newTarget] = buildNewEvent(e, 'upscale', newValue);
    onChangeProp(newEvent, newTarget);
  }

  const modeOptions = getOptions(MODE_TYPES);
  const fillTypeOptions = getOptions(FILL_TYPES, { allowEmpty: true });
  const cropTypeOptions = getOptions(CROP_TYPES, { allowEmpty: true });
  const maskTypeOptions = getOptions(MASK_TYPES);
  const aspectRatioOptions = getOptions(ASPECT_RATIO_TYPES);

  return (
    <Form className="image-query-param-builder">
      <FormGroup widths="equal">
        <Form.Input
          label="Width"
          type="number"
          name="width"
          value={width}
          onChange={onWidthChange}
          min={1}
          max={DEFAULT_MAX_WIDTH}
        />
        <Form.Input
          label="Height"
          type="number"
          name="height"
          value={height}
          onChange={onHeightChange}
          min={1}
          max={DEFAULT_MAX_HEIGHT}
          disabled={!_isEmpty(aspectRatio)}
        />
      </FormGroup>

      <FormGroup className="mode" widths="equal">
        <Form.Select
          label="Mode"
          name="mode"
          value={mode}
          options={modeOptions}
          onChange={onModeChange}
          fluid
        />
        {MODE_TYPES.includes(MODE_TYPE_FILL) && (
          <Form.Select
            label="Fill Type"
            name="fillType"
            value={fillType}
            options={fillTypeOptions}
            onChange={onChange}
            disabled={mode !== MODE_TYPE_FILL}
            fluid
          />
        )}
        <Form.Select
          label="Crop Type"
          name="cropType"
          value={cropType}
          options={cropTypeOptions}
          onChange={onChange}
          disabled={mode !== MODE_TYPE_CROP}
          fluid
        />
      </FormGroup>

      <FormGroup widths="equal">
        <Form.Select
          label="Mask"
          name="mask"
          value={mask}
          options={maskTypeOptions}
          onChange={onChange}
        />
        <Form.Select
          label="Aspect Ratio"
          name="aspectRatio"
          value={aspectRatio}
          options={aspectRatioOptions}
          onChange={onAspectRatioChange}
          disabled={mode !== MODE_TYPE_CROP}
        />
      </FormGroup>

      <FormGroup>
        <Form.Input
          label="Blur"
          type="number"
          name="blur"
          value={blur}
          onChange={onBlurChange}
          min={BLUR_MIN}
          max={BLUR_MAX}
          step={0.1}
          onKeyDown={(e) => e.preventDefault()}
          width={6}
        />

        <Form.Input
          label="Quality"
          type="number"
          name="quality"
          value={quality}
          onChange={onQualityChange}
          min={QUALITY_MIN}
          max={QUALITY_MAX}
          step={1}
          width={6}
        />

        <Form.Field
          label="Upscale"
          control={Form.Checkbox}
          name="upscale"
          checked={Boolean(upscale)}
          onChange={onUpscaleChange}
          toggle
          width={6}
        />
      </FormGroup>

      {isAdmin && (
        <FormGroup widths="equal" className="query-params-display">
          <QueryParamsDisplay formData={formData} url={url} />
        </FormGroup>
      )}
    </Form>
  );
}

ImageQueryParamBuilder.propTypes = propTypes;
ImageQueryParamBuilder.defaultProps = defaultProps;
